import { useState } from "react";
import { Link } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { TextLink } from "../components/kit/TextLink";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * SignInLinkRequestNew — issue #874, slice S2, the sign-in-link REQUEST half
 * redrawn to `SignInLink.dc.html` (design-baseline-round-5). Lives behind
 * `newUi("signin")` (`App.js`, reusing S1's flag — this slice adds no new
 * one); `SignInLinkRequest.jsx`, the shipped screen, is untouched and keeps
 * serving `/sign-in-link` with the flag off.
 *
 * PRESENTATION ONLY. Posts to the SAME endpoint (`POST /auth/sign-in-link`)
 * the shipped screen does, with the same anti-enumeration contract: the
 * response is one constant message whether or not the email is registered,
 * it is rendered verbatim and nothing else, and it never carries a token or
 * a link. Nothing here calls a different endpoint or widens what the server
 * accepts.
 *
 * CHROME. The drawing draws its own header (mark, "Compass") outside the app
 * shell — no TopBar, no nav bar (#730, same as FrontDoorNew/SignInNew,
 * D26/D35). Drawn with no `href`/button styling of its own (unlike
 * SignInNew's own header, which IS drawn as a link back to the front door on
 * its OWN artboard) — X-001 ("nothing invented"): this header stays a plain,
 * non-interactive mark rather than inventing an affordance the drawing does
 * not show.
 *
 * D48/D57 — ONE REAL BEHAVIOUR CHANGE, PRESENTATION ONLY. The shipped screen
 * toasts `formatApiErrorDetail(...) || "Request failed"` on a failed
 * request and leaves the form exactly as it was. The drawing draws a fourth
 * state, `request-failed`, that the shipped screen has no equivalent of: the
 * server's own words stay on the page, inline, with the D55 marks (alert
 * glyph + a `--nw-alert` left rule, text stays ink) — no toast. The form
 * (including the email field and the persistent "15 minutes" tile) stays
 * visible and resubmittable; only the SENT state (below) replaces it.
 *
 * DESIGNERS' NOTES ARE NOT COPY (S1's own B1 mistake, called out again for
 * this slice): every `data-dc-note` block in `SignInLink.dc.html` — the
 * dashed-border state captions ("Once it is sent — the form is replaced by
 * this", "When the request is refused or the server cannot be reached",
 * etc.) and the trailing rationale paragraphs ("The same sentence whether or
 * not that email has an account...", "The server's own words when it sends
 * them. A message that stays, not a toast (decision D48).") — are checked
 * against every state below and NONE of them render. Only the SOLID-cornered
 * content nested inside each dashed block (the confirmation sentence, the
 * spam-folder line, "Send another", "Request failed"'s message box, the
 * resubmit button) is real UI.
 */

export default function SignInLinkRequestNew() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // D48/D57 — the request's own failure message, read from the server's
  // response, never a toast. Cleared at the start of every new submit so a
  // stale banner cannot survive past the attempt that would replace it.
  const [requestError, setRequestError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setRequestError(null);
    try {
      const { data } = await api.post("/auth/sign-in-link", { email });
      setResult(data);
    } catch (err) {
      setRequestError(formatApiErrorDetail(err.response?.data?.detail) || "Request failed");
    }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      {/* Round 5 conformance (token-check) — declares its own ink token
          rather than inheriting the shell's `body` colour, the same rule
          FrontDoorNew/SignInNew's own markers state (factory #311/#312
          cannot see this yet, per the orchestrator's brief). */}
      <span data-testid="sign-in-link-request-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        data-testid="sign-in-link-page"
        className="nw-column"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
          // Round 5 conformance (token-check) — this IS the drawing's own
          // data-dc-id, sampled directly; declaring --nw-ink here rather
          // than relying on the marker span above (which token-check does
          // not sample) is what actually fixes the finding.
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--nw-ink)" }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Round 2 review (R2) — "Asking for the link" is a plain
              sibling of the title in the drawing, NOT inside a
              data-dc-note box (unlike S1's dashed-caption eyebrows) — real
              UI, caught by copy-check's live run against the captured
              default state. But the drawing's own sent-state note reads
              "Once it is sent — the form is replaced by THIS" — no
              alternate eyebrow is drawn for "sent", and the eyebrow's own
              text stops being true the moment a link has been asked for
              and sent. Scoped to !result alongside the form it labels,
              not rendered unconditionally. */}
          {!result && <div className="nw-eyebrow">Asking for the link</div>}
          <div className="nw-title">Sign in with an email link</div>
          <div className="nw-lede">
            Enter the email tied to your CricketCompass account and we'll email you a link that signs you in — no
            password needed.
          </div>
        </div>

        {!result && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  padding: "4px 10px",
                  background: "var(--nw-line-faint)",
                  border: "1px solid var(--nw-line)",
                  borderRadius: "var(--nw-radius-control)",
                }}
              >
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18, lineHeight: 1.1, color: "var(--nw-ink)" }}>
                  15
                </span>
              </div>
              <span className="nw-help">
                minutes — how long the link works for, and it works once. Asking again replaces the one before it.
              </span>
            </div>

            <form onSubmit={submit} data-testid="sign-in-link-request-form" style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)", color: "var(--nw-ink)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div className="nw-label">Email</div>
                <input
                  data-testid="sil-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="nw-kit-input"
                  style={{
                    width: "100%",
                    height: "var(--nw-size-control)",
                    borderRadius: "var(--nw-radius-control)",
                    border: "1px solid var(--nw-line)",
                    background: "var(--nw-surface)",
                    padding: "0 12px",
                    fontSize: 15,
                    color: "var(--nw-ink)",
                  }}
                />
              </div>

              {/* D48/D57 + D55 — "Request failed" is drawn as the server's
                  own words, never a fixed string; the fallback below is
                  what the screen says when the server sent none. Glyph +
                  left rule on --nw-alert only; text stays ink (no red — the
                  drawing's own rule, since the shipped #ef4444 callback
                  error is recorded as a difference, not carried over). */}
              {requestError && (
                <div
                  role="alert"
                  data-testid="sil-request-error"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "var(--nw-space-inline)",
                    padding: "10px 12px",
                    background: "var(--nw-line-faint)",
                    borderLeft: "3px solid var(--nw-alert)",
                    borderRadius: "var(--nw-radius-control)",
                  }}
                >
                  <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                  <div data-testid="sil-request-error-message" style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
                    {requestError}
                  </div>
                </div>
              )}

              <button
                type="submit"
                data-testid="sil-submit"
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: busy ? 10 : 0,
                  minHeight: "var(--nw-size-action)",
                  background: "var(--nw-gold)",
                  color: "var(--nw-ink)",
                  fontWeight: 700,
                  fontSize: 16,
                  border: "none",
                  borderRadius: "var(--nw-radius-control)",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {busy && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--nw-ink)" strokeWidth="2.5" strokeLinecap="round" className="animate-spin" aria-hidden="true">
                    <path d="M12 3a9 9 0 1 0 9 9" />
                  </svg>
                )}
                {busy ? "Sending…" : "Email me a sign-in link"}
              </button>
            </form>
          </>
        )}

        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div
              data-testid="sil-result"
              style={{
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{result.message}</div>
              <div className="nw-help" style={{ marginTop: 6 }}>
                If it doesn't arrive within a few minutes, check your spam folder before requesting another.
              </div>
            </div>
            <button
              type="button"
              data-testid="sil-send-another"
              onClick={() => {
                setResult(null);
                setEmail("");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "var(--nw-size-chip)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                color: "var(--nw-navy)",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "var(--nw-radius-control)",
                cursor: "pointer",
              }}
            >
              Send another
            </button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
          {/* R4 (review round 2) — a literal, not the imported SIGN_IN_PATH
              constant: this is the file's only /login reference, so an
              import buys nothing (S1's own R2 rule — an allowlist entry is
              what makes either shape pass, not the spelling). */}
          <TextLink
            as={Link}
            to="/login"
            data-testid="sil-back"
            style={{ gap: 6, fontSize: 13, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "none" }}
          >
            ← Back to sign in
          </TextLink>
        </div>
      </div>
    </div>
  );
}
